import { useEffect, useRef, type ReactNode } from 'react';
import { Icon, type IconName } from '../icons/Icon';

interface ModalProps {
  open: boolean;
  title: string;
  icon?: IconName;
  onClose: () => void;
  closeLabel: string;
  footer?: ReactNode;
  wide?: boolean;
  /** Beyond `wide`: `large` for two columns, `xl` for three; on a phone they fill the screen. */
  size?: 'large' | 'xl';
  /** Before the title: a marker, for the dialogs about one thing (a team). */
  lead?: ReactNode;
  /** Under the title, smaller: code, counts, time. */
  subtitle?: ReactNode;
  children: ReactNode;
}

/** Dialog with scrim; Escape and scrim click close it. Irreversible confirmations list
 *  consequences in the body instead of asking "are you sure?" (rule 17). */
export function Modal({
  open,
  title,
  icon,
  onClose,
  closeLabel,
  footer,
  wide = false,
  size,
  lead,
  subtitle,
  children,
}: ModalProps) {
  const scrim = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      // A dialog opened over another (a team's tracker over its sheet) closes alone.
      const all = document.querySelectorAll('.scrim');
      if (all[all.length - 1] === scrim.current) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      ref={scrim}
      className="scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={['modal', wide ? 'wide' : '', size ?? ''].join(' ').trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="modal-h">
          {icon === undefined ? null : (
            <Icon name={icon} size={17} style={{ color: 'var(--gold-ink)' }} />
          )}
          {lead}
          {subtitle === undefined ? (
            <b id="modal-title">{title}</b>
          ) : (
            <div style={{ minWidth: 0 }}>
              <b id="modal-title">{title}</b>
              <p className="note" style={{ margin: 0 }}>
                {subtitle}
              </p>
            </div>
          )}
          <button
            type="button"
            className="btn ico ghost"
            style={{ marginLeft: 'auto' }}
            onClick={onClose}
            aria-label={closeLabel}
          >
            <Icon name="close" size={14} />
          </button>
        </div>
        <div className="modal-b">{children}</div>
        {footer === undefined ? null : <div className="modal-f">{footer}</div>}
      </div>
    </div>
  );
}
